import axios from "axios";
import { AUTH_API_BASE_URL } from "../config/api-config";
import { getToken } from "../Compo/utilis/getToken";

// Browser side of the BankID same-device flow (login and contract signing).
// Sources: https://developers.bankid.com/how-to-guides/return-url and .../how-to-guides/autostart
//
//  - Binding key: a random key this browser keeps in localStorage and sends with every BankID
//    call. The server stores only its hash; an orderRef or return state is useless without it.
//    localStorage (not sessionStorage) so a reload or a new tab in the same browser can resume.
//  - Return state: the server sends BankID a returnUrl = this page + "#bankid-state=…". When the
//    BankID app opens it, the page resolves the state server-side (binding checked) and resumes
//    the existing order. The return itself proves nothing: completion always comes from collect.
//  - Pending operations: what this browser started, so a reload / new tab can resume it.

const BINDING_KEY = "klargo.bankid.binding";
const PENDING_KEY = "klargo.bankid.pending";
const STATE_PARAM = "bankid-state";
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{22,128}$/;
// Upper bound only; BankID orders themselves expire sooner (the server's record lives 10 min).
export const PENDING_TTL_MS = 10 * 60 * 1000;

const BASE = `${AUTH_API_BASE_URL}/BankId`;

const toBase64Url = (bytes) =>
  btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

/** This browser's binding key, created on first use. Null when storage is unavailable. */
export function getBrowserBinding() {
  try {
    let key = localStorage.getItem(BINDING_KEY);
    if (!TOKEN_PATTERN.test(key || "")) {
      const bytes = new Uint8Array(32);
      window.crypto.getRandomValues(bytes);
      key = toBase64Url(bytes);
      localStorage.setItem(BINDING_KEY, key);
      // Some private modes accept the write and drop it: then a return could never be matched.
      if (localStorage.getItem(BINDING_KEY) !== key) return null;
    }
    return key;
  } catch (_) {
    return null;
  }
}

/** Bearer header when someone is signed in (signing calls and sign returns need it). */
export function authConfig() {
  let hasToken = false;
  try { hasToken = !!localStorage.getItem("authToken"); } catch (_) { /* storage unavailable */ }
  const token = hasToken ? getToken() : null;
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

// ── Pending operations ─────────────────────────────────────────────────────────

const readPending = () => {
  try {
    const all = JSON.parse(localStorage.getItem(PENDING_KEY) || "{}");
    const now = Date.now();
    return Object.fromEntries(Object.entries(all).filter(([, op]) => op && now - op.startedAt < PENDING_TTL_MS));
  } catch (_) {
    return {};
  }
};

const writePending = (all) => {
  try { localStorage.setItem(PENDING_KEY, JSON.stringify(all)); } catch (_) { /* storage unavailable */ }
};

// Contract ids arrive as numbers (server) or strings (URL params): compare them as strings.
const normalize = (fields) => {
  const out = { ...fields };
  if (out.contractId !== undefined && out.contractId !== null) out.contractId = String(out.contractId);
  return out;
};

/** op: { orderRef, purpose: "auth" | "sign", scope?, contractId?, role? } */
export function rememberPending(op) {
  if (!op?.orderRef) return;
  const all = readPending();
  const known = all[op.orderRef];
  const fields = Object.fromEntries(Object.entries(normalize(op)).filter(([, v]) => v !== undefined));
  all[op.orderRef] = { ...known, ...fields, startedAt: known?.startedAt ?? Date.now() };
  writePending(all);
}

export function forgetPending(orderRef) {
  if (!orderRef) return;
  const all = readPending();
  delete all[orderRef];
  writePending(all);
}

/** The most recent pending operation matching `match` (e.g. { purpose: "auth", scope }). */
export function findPending(match = {}) {
  const wanted = normalize(match);
  const ops = Object.values(readPending())
    .filter((op) => Object.entries(wanted).every(([k, v]) => v === undefined || op[k] === v))
    .sort((a, b) => b.startedAt - a.startedAt);
  return ops[0] ?? null;
}

// Several dialogs can be mounted on one page; only one may resume a given order.
const claimed = new Set();
export function claimResume(orderRef) {
  if (!orderRef || claimed.has(orderRef)) return false;
  claimed.add(orderRef);
  return true;
}
export function releaseResume(orderRef) {
  claimed.delete(orderRef);
}

// ── Return from the BankID app ──────────────────────────────────────────────────

export function readReturnState() {
  const hash = window.location.hash || "";
  if (!hash.startsWith("#")) return null;
  const value = new URLSearchParams(hash.slice(1)).get(STATE_PARAM);
  return value && TOKEN_PATTERN.test(value) ? value : null;
}

// Drop the fragment so a reload or a shared link never re-presents the state.
function clearReturnState() {
  try {
    const { pathname, search } = window.location;
    window.history.replaceState(window.history.state, "", `${pathname}${search}`);
  } catch (_) { /* best-effort */ }
}

let lastResolution = null;   // { state, promise }

/**
 * Resolves "#bankid-state=…" once per state: the server maps it to this browser's order and
 * records that the return reached it. On success the order becomes a pending operation, which
 * the dialog that owns it then resumes. Returns the server's answer, or null when the page was
 * not opened by a BankID return.
 *   status: ok | notFound | bindingMismatch | loginRequired | wrongUser | error
 */
export function resolveReturnOnce() {
  const state = readReturnState();
  if (!state) return lastResolution ? lastResolution.promise : Promise.resolve(null);
  if (lastResolution?.state === state) return lastResolution.promise;

  clearReturnState();
  const browserBinding = getBrowserBinding();
  const promise = (browserBinding
    ? axios.post(`${BASE}/ResolveReturn`, { state, browserBinding }, authConfig())
        .then((res) => res.data?.result ?? res.data)
        .catch(() => ({ status: "error" }))
    : Promise.resolve({ status: "bindingMismatch" })
  ).then((result) => {
    if (result?.status === "ok" && result.orderRef) {
      rememberPending({
        orderRef: result.orderRef,
        purpose: result.purpose,
        contractId: result.contractId ?? undefined,
        role: result.role ?? undefined,
        returned: true,
      });
    }
    return result;
  });
  lastResolution = { state, promise };
  return promise;
}

// A failed return (other browser, expired, not signed in) is reported once per page, by
// whichever BankID dialog sees it first.
let reportedProblems = new WeakSet();
export function claimReturnProblem(result) {
  if (!result || result.status === "ok" || reportedProblems.has(result)) return false;
  reportedProblems.add(result);
  return true;
}

// Tests only.
export function __resetBankIdSessionForTests() {
  lastResolution = null;
  claimed.clear();
  reportedProblems = new WeakSet();
}

// ── Launching the BankID app ────────────────────────────────────────────────────

/** iPhone/iPad/Android, including iPadOS, which reports itself as a Mac with touch. */
export function isMobileDevice() {
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod|Android/i.test(ua)) return true;
  return /Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1;
}

/**
 * iPhone/iPad/iPod, including iPadOS (a "Mac" with touch). Every browser there is WebKit, which
 * hands an app.bankid.com link to the BankID app only when the user's own tap opens it — a launch
 * made later, after an API call returns, opens BankID's "couldn't be launched" page instead.
 */
export function isIOSDevice() {
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  return /Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1;
}

/**
 * BankID's documented launch: on mobile browsers the https://app.bankid.com universal/app link,
 * opened through an anchor with referrerPolicy "origin"; on desktop the bankid:/// scheme.
 * No "redirect" parameter: it is deprecated — the server sends returnUrl when creating the order.
 */
export function bankIdLaunchUrl(autoStartToken, mobile = isMobileDevice()) {
  const token = encodeURIComponent(autoStartToken);
  return mobile
    ? `https://app.bankid.com/?autostarttoken=${token}`
    : `bankid:///?autostarttoken=${token}`;
}

export function openBankIdApp(autoStartToken) {
  if (!autoStartToken) return false;
  const mobile = isMobileDevice();
  const link = document.createElement("a");
  link.href = bankIdLaunchUrl(autoStartToken, mobile);
  if (mobile) link.referrerPolicy = "origin";
  link.rel = "noopener";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  return true;
}

/** Path + query of this page; the server validates it and returns the user here. */
export function currentReturnPath() {
  return `${window.location.pathname}${window.location.search}`;
}

export const BANKID_STATE_PARAM = STATE_PARAM;
